import {
  getMapRecord,
  getMapRecords,
  getPointer,
  iterateRecordMap,
  PointerWithRecord,
  RecordMap,
  RecordPointer,
  RecordTable,
  setMapRecord,
} from "libs/schema";
import {
  GetRecordResult,
  GetRecordsResult,
  RecordLoaderApi,
  SharedQueryApiKey,
  SharedQueryParams,
  getCacheKey,
} from "./queries";
import { DeferredPromise } from "libs/promise-utils";
import { BatchedQueue } from "libs/BatchedQueue";
import { Semaphore } from "libs/semaphore";

/**
 *
 * @returns a database proxy that caches requested records in the provided
 *   recordMap (or an internal recordMap, if one is not provided). If the request
 *   has already been cached, then the cached value will be returned and a new
 *   request will not be made. Note that the provided recordMap will be mutated.
 */
export function createMemoizedDatabase(
  db: RecordLoaderApi,
  props: {
    /**
     * IMPORTANT! If provided, this recordMap will be mutated and
     * fetched records will be added to it.
     */
    recordMap?: RecordMap;

    /**
     * If provided, the provided cache will be used instead of an internal one.
     * This will supercede any provided recordMap.
     */
    cache?: QueryCache;

    /**
     * The maximum number of concurrent database connections to allow.
     * @default 5
     */
    concurrency?: number;
  } = {},
): RecordLoaderApi {
  const { recordMap = {}, cache, concurrency = 5 } = props;

  const databaseCache = cache ?? new QueryCache(recordMap);

  const semaphore = new Semaphore(concurrency);

  const proxy: RecordLoaderApi = new Proxy({} as unknown as RecordLoaderApi, {
    get(_, prop: keyof RecordLoaderApi) {
      if (prop in db && typeof db[prop] !== "function") return db[prop];

      switch (prop) {
        case "getRecord": {
          return getRecord;
        }
        case "getRecords": {
          return getRecords;
        }
        default: {
          return (params: any) => {
            return getQueryApi({
              name: prop,
              params,
            });
          };
        }
      }
    },
  });

  /**
   * Calls to enqueue load the result into the database cache but don't directly return the records.
   * Access the records through the database cache instead.
   */
  const getRecordsQueue = new BatchedQueue<{ pointers: RecordPointer<any>[]; includeSoftDeletes: true }, undefined>({
    // In addition to setting the `maxParallel` property, we're also wrapping the `processBatch`
    // function in a semaphore. This ensures that the combined `getRecordsQueue` and `getQueryApi` requests
    // don't exceed the concurrency limit.
    processBatch: semaphore.wrap(async (argsBatch) => {
      const pointers = argsBatch.flatMap((batch) => batch.pointers);

      const [pointersWithRecords] = await db.getRecords(pointers, { includeSoftDeletes: true });

      databaseCache.loadRecordMap(pointersWithRecords);

      return Array.from({ length: argsBatch.length }, () => undefined);
    }),
    maxBatchSize: 1000,
    maxParallel: concurrency,
    delayMs: 1,
  });

  const getRecord = async <T extends RecordTable>(
    a: T | RecordPointer<T>,
    b?: string | { includeSoftDeletes?: boolean },
    c?: { includeSoftDeletes?: boolean },
  ): Promise<GetRecordResult<T>> => {
    const pointer = typeof a === "string" ? { table: a, id: b as string } : getPointer(a);
    const options = typeof b === "object" ? b : c;

    const query = () =>
      // We always fetch with soft deletes from the database and then we filter out the soft
      // deleted records below.
      getRecordsQueue.enqueue({ pointers: [pointer], includeSoftDeletes: true }).then(() => {
        const record = getMapRecord(databaseCache.recordMap, pointer);
        return record || null;
      });

    const record = await databaseCache.load({
      params: { type: "getRecord", params: pointer },
      query,
    });

    if (record?.deleted_at && !options?.includeSoftDeletes) {
      return [null];
    }

    return [record];
  };

  const getRecords: RecordLoaderApi["getRecords"] = async <T extends RecordTable>(
    pointers: RecordPointer<T>[],
    options?: { includeSoftDeletes?: boolean },
  ): Promise<GetRecordsResult<T>> => {
    const fetchPointers: RecordPointer[] = [];
    const cachedQueries: Array<Promise<PointerWithRecord<T> | undefined>> = [];

    for (const _pointer of pointers) {
      // We use `getPointer` to sanitize the incoming arguments in case the object has
      // extra properties that would cause the cache key to be incorrect.
      const pointer = getPointer<any>(_pointer);
      const key = getCacheKey({ type: "getRecord", params: pointer });

      const existingQuery = databaseCache.queryCache.get(key);

      if (existingQuery) {
        cachedQueries.push(
          existingQuery.promise.then((record) => {
            if (!record) return;
            return { ...pointer, record };
          }),
        );
      } else {
        const deferred = new DeferredPromise();
        databaseCache.queryCache.set(key, deferred);
        fetchPointers.push(pointer);
      }
    }

    const promises: Promise<unknown>[] = [];
    // Note that, given the fetch strategy below, the results will be out of order.
    // Currently the results aren't expected to be in a particular order though.
    let results: PointerWithRecord<T>[] = [];

    if (cachedQueries.length > 0) {
      const getCachedResults = async () => {
        const cachedQueryResults = await Promise.all(cachedQueries);

        for (const result of cachedQueryResults) {
          if (!result) continue;
          results.push(result);
        }
      };

      promises.push(getCachedResults());
    }

    if (fetchPointers.length > 0) {
      const getUncachedResults = async () => {
        // We always fetch with soft deletes from the database and then we filter out the soft
        // deleted records below.
        await getRecordsQueue.enqueue({ pointers: fetchPointers, includeSoftDeletes: true });

        const pointersWithRecord = getMapRecords<any>(databaseCache.recordMap, fetchPointers);

        // Enqueue will resolve any pending cached queries for the pointers that were returned,
        // but pointers that were not found in the database will not have been resolved. In this
        // case we need to resolve them manually here.
        for (const pointer of fetchPointers) {
          const key = getCacheKey({ type: "getRecord", params: pointer });
          const deferred = databaseCache.queryCache.get(key);
          deferred?.resolve(getMapRecord(databaseCache.recordMap, pointer) ?? null);
        }

        results.push(...pointersWithRecord);
      };

      promises.push(getUncachedResults());
    }

    await Promise.all(promises);

    if (!options?.includeSoftDeletes) {
      results = results.filter((r) => !r.record.deleted_at);
    }

    return [results];
  };

  function getQueryApi<N extends SharedQueryApiKey>(props: { name: N; params: SharedQueryParams<N> }) {
    const query = semaphore.wrap(async () => {
      const [records, { recordMap }] = await db[props.name](props.params as any);

      databaseCache.loadRecordMap(recordMap);

      return [records, { recordMap }];
    });

    const params = {
      type: props.name,
      params: props.params as Record<string, unknown>,
    };

    return databaseCache.load({
      params,
      query,
    });
  }

  return proxy;
}

export class QueryCache {
  queryCache = new Map<string, DeferredPromise<any>>();

  constructor(public recordMap: RecordMap = {}) {
    for (const pointerWithRecord of iterateRecordMap(recordMap)) {
      const { record, ...pointer } = pointerWithRecord;

      setMapRecord(this.recordMap, pointer, record);

      const key = getCacheKey({ type: "getRecord", params: pointer });

      const deferred = new DeferredPromise();
      deferred.resolve(record);
      this.queryCache.set(key, deferred);
    }
  }

  /**
   * This method will return records from the CachedRecordLoader's recordMap
   * if they exist. Otherwise, it will fetch them from the database and
   * add them to the recordMap.
   */
  async load<T>(props: {
    params: { type: string; params: Record<string, unknown> };
    query: () => Promise<T>;
  }): Promise<T> {
    const { params, query } = props;

    const key = getCacheKey({
      type: params.type,
      params: params.params,
      omitParams: [],
    });

    const existingQuery = this.queryCache.get(key) as DeferredPromise<T> | undefined;

    if (existingQuery) return existingQuery.promise;

    const deferred = new DeferredPromise<T>();

    this.queryCache.set(key, deferred);

    query().then(deferred.resolve, deferred.reject);

    return deferred.promise;
  }

  loadRecordMap(recordMap: RecordMap | PointerWithRecord[]) {
    const pointerWithRecords = Array.isArray(recordMap) ? recordMap : iterateRecordMap(recordMap);

    for (const pointerWithRecord of pointerWithRecords) {
      const { record, ...pointer } = pointerWithRecord;

      // We don't overwrite existing records in the recordMap. This allows us to optionally
      // preload the cache with uncommitted records and not worry about having those records
      // be overwritten.
      if (!getMapRecord(this.recordMap, pointer)) {
        setMapRecord(this.recordMap, pointer, record);
      }

      const key = getCacheKey({
        type: "getRecord",
        params: pointer,
      });

      const existingQuery = this.queryCache.get(key);
      // Get the record from the cache to in case we ignored the incoming record
      const cachedRecord = getMapRecord(this.recordMap, pointer);

      if (existingQuery) {
        existingQuery.resolve(cachedRecord);
      } else {
        const deferred = new DeferredPromise();
        deferred.resolve(cachedRecord);
        this.queryCache.set(key, deferred);
      }
    }
  }
}
